import { borders } from 'src/theme/adSales/borders'

const defaultButton = {
  cursor: 'pointer',
  borderRadius: '0px',
  width: 'auto',
  minHeight: '48px',
  minWidth: '48px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 'labels',
  lineHeight: 'labels',
  fontWeight: 'bold',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.4s ease, border-bottom 0.4s ease'
}

const navigationTab = {
  ...defaultButton,
  justifyContent: 'normal',
  height: 'auto',
  width: '100%',
  py: 6,
  pr: 5,
  pl: [7, null, 8],
  textAlign: 'left' as const,
  fontWeight: 'normal'
}

export const buttons = {
  primary: {
    ...defaultButton,
    backgroundColor: 'background.primary',
    color: 'text.quaternary',
    '&:hover': { border: '1px solid black' },
    '&:focus-visible': {
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none'
    }
  },
  primaryDark: {
    ...defaultButton,
    backgroundColor: 'background.quaternary',
    color: 'text.primary',
    border: 'none',
    '&:hover': { border: '1px solid white' },
    '&:focus-visible': {
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none'
    }
  },
  secondary: {
    ...defaultButton,
    backgroundColor: 'background.quaternary',
    color: 'text.primary',
    border: '2px solid black',
    '&:hover': { backgroundColor: 'background.primary', color: 'text.quaternary' },
    '&:focus-visible': {
      backgroundColor: 'background.primary',
      color: 'text.quaternary',
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: '2px solid black',
      '&:hover': { backgroundColor: 'background.quaternary', color: 'text.primary' }
    }
  },
  secondaryDark: {
    ...defaultButton,
    backgroundColor: 'background.primary',
    color: 'text.quaternary',
    border: '2px solid white',
    '&:hover': { backgroundColor: 'background.quaternary', color: 'text.primary' },
    '&:focus-visible': {
      backgroundColor: 'background.quaternary',
      color: 'text.primary',
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: '2px solid white',
      '&:hover': { backgroundColor: 'background.primary', color: 'text.quaternary' }
    }
  },
  tertiary: {
    ...defaultButton,
    backgroundColor: 'background.quaternary',
    color: 'text.primary',
    border: 'none',
    '&:hover': { backgroundColor: 'background.tertiary' },
    '&:focus-visible': {
      backgroundColor: 'background.tertiary',
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none',
      backgroundColor: 'background.quaternary'
    }
  },
  tertiaryDark: {
    ...defaultButton,
    backgroundColor: 'background.primary',
    color: 'text.quaternary',
    border: 'none',
    '&:hover': { backgroundColor: 'background.secondary' },
    '&:focus-visible': {
      backgroundColor: 'background.secondary',
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none',
      backgroundColor: 'background.primary'
    }
  },
  link: {
    ...defaultButton,
    backgroundColor: 'background.quaternary',
    color: 'text.primary',
    border: 'none',
    '&:hover': { color: 'text.secondary' },
    '&:focus-visible': {
      border: borders.tertiaryThin,
      boxShadow: borders.activeButton,
      textDecoration: 'none'
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none',
      backgroundColor: 'background.quaternary',
      '&:hover': { color: 'text.primary' }
    },
    textDecoration: 'underline'
  },
  linkDark: {
    ...defaultButton,
    backgroundColor: 'background.primary',
    color: 'text.quaternary',
    border: 'none',
    '&:hover': { color: 'text.tertiary' },
    '&:focus-visible': {
      backgroundColor: 'background.quaternary',
      color: 'text.primary',
      border: borders.tertiaryThin,
      boxShadow: borders.activeButton,
      textDecoration: 'none'
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none',
      backgroundColor: 'background.primary',
      '&:hover': { color: 'text.quaternary' }
    },
    textDecoration: 'underline'
  },
  navigation: {
    ...defaultButton,
    height: '100%',
    width: ['100%', null, null, null, 'auto'],
    textAlign: 'left' as const,
    px: 3,
    pt: [3, null, null, null, 6],
    pb: [3, null, null, null, 5],
    display: 'inline-block',
    backgroundColor: 'background.quaternary',
    borderBottom: borders.quaternaryMediumHidden,
    color: 'text.primary',
    fontWeight: 'medium',
    '&:hover': {
      backgroundColor: 'background.tertiary',
      borderBottom: borders.tertiaryMediumHidden
    },
    '&:focus-visible': {
      backgroundColor: 'background.tertiary',
      borderBottom: borders.tertiaryMediumHidden,
      boxShadow: borders.activeButton
    },
    '&.navigationActive': {
      borderBottom: borders.primaryMedium
    }
  },
  navigationTabPrimary: {
    ...navigationTab,
    borderBottom: borders.tertiaryThin,
    borderLeft: borders.quaternaryThickHidden,
    backgroundColor: 'background.quaternary',
    color: 'text.secondary',
    '&:active': {
      borderLeft: borders.secondaryThick,
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&.selected': {
      borderLeft: borders.secondaryThick,
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&:hover': {
      backgroundColor: 'background.tertiary',
      borderLeft: borders.tertiaryThickHidden,
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&:focus-visible': { boxShadow: borders.activeButton }
  },
  navigationTabSecondary: {
    ...navigationTab,
    borderBottom: borders.tertiaryThin,
    borderLeft: borders.tertiaryThickHidden,
    backgroundColor: 'background.tertiary',
    color: 'text.secondary',
    '&:active': {
      borderLeft: borders.secondaryThick,
      color: 'text.secondary',
      fontWeight: 'bold'
    },
    '&.selected': {
      borderLeft: borders.secondaryThick,
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&:hover': {
      backgroundColor: 'background.secondary',
      borderLeft: borders.secondaryThickHidden,
      color: 'text.quaternary',
      fontWeight: 'bold'
    },
    '&:focus-visible': { boxShadow: borders.activeButton }
  },
  navigationTabTertiary: {
    ...navigationTab,
    borderBottom: borders.tertiaryThin,
    backgroundColor: 'background.primary',
    borderLeft: borders.primaryThickHidden,
    color: 'text.quaternary',
    '&:active': {
      borderLeft: borders.quaternaryThick,
      color: 'text.quaternary',
      fontWeight: 'bold'
    },
    '&.selected': {
      borderLeft: borders.quaternaryThick,
      color: 'text.quaternary',
      fontWeight: 'bold'
    },
    '&:hover': {
      backgroundColor: 'background.secondary',
      color: 'text.quaternary',
      borderLeft: borders.secondaryThickHidden
    },
    '&:focus-visible': { boxShadow: borders.activeButton }
  },
  navigationMobileTabPrimary: {
    ...navigationTab,
    color: 'text.secondary',
    backgroundColor: 'background.quaternary',
    borderLeft: borders.quaternaryThickHidden,
    borderBottom: borders.tertiaryThin,
    '&:active': {
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&.selected': {
      color: 'text.primary',
      fontWeight: 'bold'
    }
  },
  navigationMobileTabSecondary: {
    ...navigationTab,
    color: 'text.secondary',
    backgroundColor: 'background.tertiary',
    borderLeft: borders.tertiaryThickHidden,
    borderBottom: borders.tertiaryThin,
    '&:active, &:focus': {
      color: 'text.primary',
      fontWeight: 'bold'
    },
    '&.selected': {
      color: 'text.primary',
      fontWeight: 'bold'
    }
  },
  navigationMobileTabTertiary: {
    ...navigationTab,
    color: 'text.tertiary',
    backgroundColor: 'background.primary',
    borderLeft: borders.primaryThickHidden,
    borderBottom: borders.tertiaryThin,
    '&:active, &:focus': {
      color: 'text.quaternary',
      fontWeight: 'bold'
    },
    '&.selected': {
      color: 'text.quaternary',
      fontWeight: 'bold'
    }
  },
  filterTab: {
    ...defaultButton,
    backgroundColor: 'background.quaternay',
    color: 'text.primary',
    maxHeight: '55px',
    '&:hover': { borderBottom: '4px solid #BDBDBD' },
    '&:focus-visible': {
      boxShadow: borders.activeButton
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'default',
      border: 'none'
    }
  },
  longDropdown: {
    ...defaultButton,
    height: '100%',
    width: ['100%', null, null, null, '100%'],
    textAlign: 'left' as const,
    pr: [3],
    pl: [0],
    pt: [3, null, null, null, 6],
    pb: [3, null, null, null, 5],
    display: 'inline-block',
    backgroundColor: 'background.quaternary',
    borderBottom: borders.quaternaryMediumHidden,
    color: 'text.primary',
    fontWeight: 'medium',
    '&:hover': {
      backgroundColor: 'background.tertiary',
      borderBottom: borders.tertiaryMediumHidden
    },
    '&:focus-visible': {
      backgroundColor: 'background.tertiary',
      borderBottom: borders.tertiaryMediumHidden,
      boxShadow: borders.activeButton
    },
    '&.navigationActive': {
      borderBottom: borders.primaryMedium
    }
  }
}

type ThemeButtons = typeof buttons

export type ButtonVariants = `buttons.${keyof Pick<
  ThemeButtons,
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link'
  | 'primaryDark'
  | 'secondaryDark'
  | 'tertiaryDark'
  | 'linkDark'
  | 'navigation'
  | 'navigationTabPrimary'
  | 'navigationTabSecondary'
  | 'navigationTabTertiary'
  | 'navigationMobileTabPrimary'
  | 'navigationMobileTabSecondary'
  | 'navigationMobileTabTertiary'
  | 'filterTab'
  | 'longDropdown'
>}`
